// src/utils/tableauProblemes.ts — SCE Issues
// Le tableau de « Tous les problèmes » (/issues, 02/10) : colonnes au choix, tri,
// filtres sur les en-têtes, regroupement, largeurs automatiques. Pur, testable.
//
// Maquette validée : https://claude.ai/artifact/4vdMRrVNXV48nwHuSEDhLg
//
// ─── Ce qui est mémorisé, et où ───
//
// Tout `ReglagesTableau` (colonnes, ordre, largeurs fixées à la main, tri,
// regroupement, densité, filtres) vit dans le bloc `listeProblemes` du JSON
// `Prefs` de la ligne de l'utilisateur (userPrefs.api). Aucune colonne SharePoint
// à créer. Le texte de la recherche, lui, n'est pas mémorisé.
//
// ─── Règles qui ne bougent pas ───
//
// - Les annulés n'apparaissent que si « Annulé » est coché dans le filtre Statut
//   (demande 3 : ils sortent des vues actives par défaut, jamais hors de portée).
// - Rapprocher un N° de série, un OF ou un PN : TOUJOURS `cleRapprochement`.
// - Les valeurs de filtre mémorisées sont des CODES (ELEC, Critique, Clos…), pas
//   des libellés d'affichage : un libellé peut changer sans casser un réglage.

import { METIERS_LABELS_COURT, METIERS_ORDRE } from '@/config/metiers.config'
import { SUJETS_CONFIG, libelleCle, rangeeMetiersEtSujets, type ClePerimetre } from '@/config/sujets.config'
import { DEFINITIONS_BADGES } from '@/config/badgesCloture.config'
import {
  BADGES_CLOTURE,
  CRITICITES_ORDRE,
  PHASES_ORDRE,
  SANS_BADGE,
  STATUTS_ORDRE,
  SUJETS,
  cleRapprochement,
  compteCommeProbleme,
  type Issue,
  type Metier,
  type Sujet,
} from '@/types/issue.types'
import { estDeclarePar } from '@/utils/identiteDeclarant'

// ─── Types ───

export const COLONNES_IDS = [
  'id',
  'titre',
  'metier',
  'machine',
  'of',
  'pn',
  'modele',
  'famille',
  'phase',
  'criticite',
  'statut',
  'etat',
  'badge',
  'declarant',
  'date',
  'age',
  'cloture',
] as const

export type ColonneId = (typeof COLONNES_IDS)[number]
export type SensTri = 'asc' | 'desc'
export type Regroupement = 'machine' | 'pn' | 'metier' | 'statut'
export type Densite = 'compact' | 'confortable'

export interface CritereTri {
  col: ColonneId
  sens: SensTri
}

export type FiltresColonnes = Partial<Record<ColonneId, string[]>>

export interface ReglagesTableau {
  /** 2 (02/10) : colonne « État » à la place de Criticité + Statut, option d'extrait. */
  version: 2
  /** TOUTES les colonnes, visibles ou non, dans l'ordre d'affichage. */
  ordre: ColonneId[]
  visibles: ColonneId[]
  /** Largeurs fixées à la main (px). Absente = largeur automatique. */
  largeurs: Partial<Record<ColonneId, number>>
  /** Vide = par défaut (déclaré le, du plus récent au plus ancien). */
  tri: CritereTri[]
  regroupement: Regroupement | null
  densite: Densite
  filtres: FiltresColonnes
  /** Vue « Mes déclarations » : les problèmes déclarés par la personne affichée. */
  mesDeclarations: boolean
  /** Un extrait de la description à la suite du titre (désactivé par défaut). */
  extrait: boolean
  /** Vue « Suivis » : la cloche du problème, ou un équipement / projet suivi (cf. motifSuivi). */
  suivis: boolean
  /** Vues personnelles, dans l'ordre de création. */
  vues: VuePerso[]
}

/** Une vue enregistrée par la personne : ses filtres, son tri, son regroupement, sous un nom. */
export interface VuePerso {
  id: string
  nom: string
  filtres: FiltresColonnes
  tri: CritereTri[]
  regroupement: Regroupement | null
  mesDeclarations: boolean
  suivis: boolean
}

export const MAX_VUES_PERSO = 12
export const LONGUEUR_MAX_NOM_VUE = 40

/** Ce que le tableau doit savoir du monde extérieur. */
export interface ContexteTableau {
  /** Modèle d'un problème, lu dans le référentiel par l'appelant. */
  modeleDe: (issue: Issue) => string
  /** Instant de référence (ms) pour l'âge ; injecté pour les tests. */
  maintenant: number
  /** Identités du déclarant affiché (`identitesDeclarant`), ou null. */
  mesIdentites: readonly string[] | null
  /** La personne affichée suit-elle ce problème ? (même règle qu'« Éléments suivis ») */
  estSuivi: (issue: Issue) => boolean
}

// ─── Catalogue des colonnes ───

export interface DefColonne {
  id: ColonneId
  libelle: string
  /** Largeur minimale (px), sous laquelle une colonne ne rétrécit jamais. */
  min: number
  /** Plafond de la largeur automatique. */
  max: number
  /** Sens du premier clic sur l'en-tête. */
  premierSens: SensTri
  filtrable: boolean
  /** Ordre canonique des valeurs de filtre ; sinon alphabétique. */
  valeurs?: readonly string[]
  /** Toujours affichée (le titre du problème). */
  verrouillee?: boolean
  /** Lit le référentiel Windchill : masquée pour un fournisseur. */
  referentiel?: boolean
  droite?: boolean
  /** Place ajoutée autour du texte mesuré (pastille, point de couleur). */
  extra: number
  /**
   * Vocabulaire fermé (statuts, dates…) : la plus large valeur fait foi. Texte
   * libre (modèle, déclarant) : le 90e centile, pour qu'une valeur exceptionnelle
   * n'élargisse pas toute la colonne.
   */
  vocabulaireFerme: boolean
}

const BUCKETS_DATE = ['Moins de 7 jours', '7 à 30 jours', 'Plus de 30 jours'] as const
export const SANS_VALEUR = 'Non renseigné'

export const DEFINITIONS_COLONNES: Record<ColonneId, DefColonne> = {
  id: { id: 'id', libelle: 'Réf.', min: 76, max: 130, premierSens: 'asc', filtrable: false, extra: 0, vocabulaireFerme: true },
  titre: { id: 'titre', libelle: 'Problème', min: 180, max: 240, premierSens: 'asc', filtrable: false, verrouillee: true, extra: 0, vocabulaireFerme: false },
  metier: { id: 'metier', libelle: 'Métier', min: 76, max: 140, premierSens: 'asc', filtrable: true, valeurs: rangeeMetiersEtSujets(METIERS_ORDRE, SUJETS), extra: 18, vocabulaireFerme: true },
  machine: { id: 'machine', libelle: 'N° de série', min: 84, max: 160, premierSens: 'asc', filtrable: true, extra: 0, vocabulaireFerme: true },
  of: { id: 'of', libelle: 'OF', min: 84, max: 160, premierSens: 'asc', filtrable: true, extra: 0, vocabulaireFerme: true },
  pn: { id: 'pn', libelle: 'PN', min: 70, max: 120, premierSens: 'asc', filtrable: true, extra: 0, vocabulaireFerme: true },
  modele: { id: 'modele', libelle: 'Modèle', min: 120, max: 240, premierSens: 'asc', filtrable: true, referentiel: true, extra: 0, vocabulaireFerme: false },
  famille: { id: 'famille', libelle: 'Famille', min: 120, max: 240, premierSens: 'asc', filtrable: true, extra: 0, vocabulaireFerme: false },
  phase: { id: 'phase', libelle: 'Phase', min: 80, max: 140, premierSens: 'asc', filtrable: true, valeurs: PHASES_ORDRE, extra: 0, vocabulaireFerme: true },
  criticite: { id: 'criticite', libelle: 'Criticité', min: 90, max: 150, premierSens: 'asc', filtrable: true, valeurs: CRITICITES_ORDRE, extra: 22, vocabulaireFerme: true },
  statut: { id: 'statut', libelle: 'Statut', min: 80, max: 140, premierSens: 'asc', filtrable: true, valeurs: STATUTS_ORDRE, extra: 22, vocabulaireFerme: true },
  // Criticité + statut en icônes, libellés au survol. Son filtre écrit dans
  // ceux de Statut et de Criticité : il n'a pas de valeurs à lui.
  etat: { id: 'etat', libelle: 'État', min: 64, max: 100, premierSens: 'asc', filtrable: true, extra: 42, vocabulaireFerme: true },
  badge: { id: 'badge', libelle: 'Badge', min: 76, max: 140, premierSens: 'asc', filtrable: true, valeurs: [...BADGES_CLOTURE, SANS_BADGE], extra: 40, vocabulaireFerme: true },
  declarant: { id: 'declarant', libelle: 'Déclarant', min: 130, max: 220, premierSens: 'asc', filtrable: true, extra: 0, vocabulaireFerme: false },
  date: { id: 'date', libelle: 'Déclaré le', min: 76, max: 130, premierSens: 'desc', filtrable: true, valeurs: BUCKETS_DATE, droite: true, extra: 0, vocabulaireFerme: true },
  age: { id: 'age', libelle: 'Âge', min: 60, max: 100, premierSens: 'desc', filtrable: true, valeurs: BUCKETS_DATE, droite: true, extra: 0, vocabulaireFerme: true },
  cloture: { id: 'cloture', libelle: 'Clos le', min: 76, max: 130, premierSens: 'desc', filtrable: false, droite: true, extra: 0, vocabulaireFerme: true },
}

/** Colonnes d'origine : l'État tout à droite, pour alléger la ligne. */
export const COLONNES_VISIBLES_DEFAUT: readonly ColonneId[] = ['id', 'titre', 'badge', 'date', 'etat']
export const ORDRE_DEFAUT: readonly ColonneId[] = [
  ...COLONNES_VISIBLES_DEFAUT,
  'metier',
  'machine',
  'of',
  'pn',
  'modele',
  'famille',
  'phase',
  'criticite',
  'statut',
  'declarant',
  'age',
  'cloture',
]

/** Les filtres que porte l'en-tête « État ». */
export const FILTRES_ETAT: readonly ColonneId[] = ['statut', 'criticite']

export const REGROUPEMENTS: readonly Regroupement[] = ['machine', 'pn', 'metier', 'statut']
export const LIBELLE_REGROUPEMENT: Record<Regroupement, string> = {
  machine: 'N° de série',
  pn: 'PN',
  metier: 'métier',
  statut: 'statut',
}
/** Pluriel de ce que regroupe chaque mode (« 12 machines »). */
export const PLURIEL_REGROUPEMENT: Record<Regroupement, [string, string]> = {
  machine: ['équipement', 'équipements'],
  pn: ['projet', 'projets'],
  metier: ['métier', 'métiers'],
  statut: ['statut', 'statuts'],
}

export function reglagesParDefaut(): ReglagesTableau {
  return {
    version: 2,
    ordre: [...ORDRE_DEFAUT],
    visibles: [...COLONNES_VISIBLES_DEFAUT],
    largeurs: {},
    tri: [],
    regroupement: 'machine',
    densite: 'compact',
    filtres: {},
    mesDeclarations: false,
    extrait: false,
    suivis: false,
    vues: [],
  }
}

/** Les colonnes d'origine seulement : ordre, visibilité, largeurs et densité. */
export function colonnesDOrigine(r: ReglagesTableau): ReglagesTableau {
  const d = reglagesParDefaut()
  return { ...r, ordre: d.ordre, visibles: d.visibles, largeurs: {}, densite: d.densite }
}

// ─── Lecture tolérante des réglages mémorisés ───

const estTexte = (v: unknown): v is string => typeof v === 'string'

/**
 * Remet en forme ce qu'on a lu : un JSON ancien, retouché ou corrompu ne doit
 * jamais faire planter la page ni injecter une colonne que plus rien ne sait
 * afficher. Une absence de regroupement ENREGISTRÉE (null) est respectée ;
 * l'absence de la clé donne le regroupement par défaut.
 *
 * @param interne faux pour un fournisseur : pas de colonne du référentiel.
 */
export function lireReglages(brut: unknown, interne = true): ReglagesTableau {
  const defaut = reglagesParDefaut()
  const r = brut && typeof brut === 'object' && !Array.isArray(brut) ? (brut as Record<string, unknown>) : null
  const version = r?.['version']
  if (!r || (version !== 1 && version !== 2)) return garderAutorisees(defaut, interne)

  const connue = (v: unknown): v is ColonneId => estTexte(v) && (COLONNES_IDS as readonly string[]).includes(v)
  const ordre = (Array.isArray(r['ordre']) ? r['ordre'] : []).filter(connue)
  const ordreFinal = [...new Set(ordre)]
  // Une colonne arrivée depuis l'enregistrement se range à la fin (l'État, en V1).
  for (const id of ORDRE_DEFAUT) if (!ordreFinal.includes(id)) ordreFinal.push(id)

  let visibles = [...new Set((Array.isArray(r['visibles']) ? r['visibles'] : []).filter(connue))]
  // V1 → V2 : Criticité et Statut cèdent la place à l'État, tout à droite.
  if (version === 1) {
    const avaitEtat = visibles.includes('criticite') || visibles.includes('statut')
    visibles = visibles.filter((c) => c !== 'criticite' && c !== 'statut')
    if (avaitEtat && !visibles.includes('etat')) visibles.push('etat')
  }
  if (!visibles.includes('titre')) visibles.push('titre')

  const largeurs: ReglagesTableau['largeurs'] = {}
  const lw = r['largeurs']
  if (lw && typeof lw === 'object') {
    for (const [k, v] of Object.entries(lw as Record<string, unknown>)) {
      if (connue(k) && typeof v === 'number' && Number.isFinite(v) && k !== 'titre') {
        largeurs[k] = Math.round(Math.min(600, Math.max(DEFINITIONS_COLONNES[k].min, v)))
      }
    }
  }

  const tri = lireTri(r['tri'])
  const filtres = lireFiltres(r['filtres'])
  const regroupement = 'regroupement' in r ? lireRegroupement(r['regroupement']) : defaut.regroupement

  const vues: VuePerso[] = []
  for (const brute of Array.isArray(r['vues']) ? r['vues'] : []) {
    const v = brute && typeof brute === 'object' ? (brute as Record<string, unknown>) : null
    const nom = typeof v?.['nom'] === 'string' ? v['nom'].trim().slice(0, LONGUEUR_MAX_NOM_VUE) : ''
    const id = typeof v?.['id'] === 'string' ? v['id'] : ''
    if (!v || !nom || !id || vues.some((x) => x.id === id) || vues.length >= MAX_VUES_PERSO) continue
    vues.push({
      id,
      nom,
      filtres: lireFiltres(v['filtres']),
      tri: lireTri(v['tri']),
      regroupement: lireRegroupement(v['regroupement']),
      mesDeclarations: v['mesDeclarations'] === true,
      suivis: v['suivis'] === true,
    })
  }

  return garderAutorisees(
    {
      version: 2,
      ordre: ordreFinal,
      visibles,
      largeurs,
      tri,
      regroupement,
      densite: r['densite'] === 'confortable' ? 'confortable' : 'compact',
      filtres,
      mesDeclarations: r['mesDeclarations'] === true,
      extrait: r['extrait'] === true,
      suivis: r['suivis'] === true,
      vues,
    },
    interne,
  )
}

const colonneConnue = (v: unknown): v is ColonneId => estTexte(v) && (COLONNES_IDS as readonly string[]).includes(v)

function lireTri(brut: unknown): CritereTri[] {
  const tri: CritereTri[] = []
  for (const t of Array.isArray(brut) ? brut : []) {
    const c = t as Record<string, unknown> | null
    if (c && colonneConnue(c['col']) && (c['sens'] === 'asc' || c['sens'] === 'desc') && !tri.some((x) => x.col === c['col'])) {
      tri.push({ col: c['col'], sens: c['sens'] })
    }
  }
  return tri
}

function lireFiltres(brut: unknown): FiltresColonnes {
  const filtres: FiltresColonnes = {}
  if (!brut || typeof brut !== 'object') return filtres
  for (const [k, v] of Object.entries(brut as Record<string, unknown>)) {
    if (!colonneConnue(k) || !DEFINITIONS_COLONNES[k].filtrable || !Array.isArray(v)) continue
    const valeurs = [...new Set(v.filter(estTexte))]
    if (valeurs.length > 0) filtres[k] = valeurs
  }
  return filtres
}

function lireRegroupement(brut: unknown): Regroupement | null {
  return REGROUPEMENTS.includes(brut as Regroupement) ? (brut as Regroupement) : null
}

/** Un fournisseur ne lit pas le référentiel Windchill : ni sa colonne, ni son filtre. */
function garderAutorisees(r: ReglagesTableau, interne: boolean): ReglagesTableau {
  if (interne) return r
  const interdite = (c: ColonneId): boolean => DEFINITIONS_COLONNES[c].referentiel === true
  const filtres: FiltresColonnes = {}
  for (const [k, v] of Object.entries(r.filtres) as [ColonneId, string[]][]) if (!interdite(k)) filtres[k] = v
  return {
    ...r,
    visibles: r.visibles.filter((c) => !interdite(c)),
    tri: r.tri.filter((t) => !interdite(t.col)),
    filtres,
    // Un fournisseur ne suit rien (la cloche est réservée aux internes).
    suivis: false,
  }
}

export const colonnesProposees = (interne: boolean): ColonneId[] =>
  ORDRE_DEFAUT.filter((c) => interne || !DEFINITIONS_COLONNES[c].referentiel)

// ─── Valeurs d'un problème, colonne par colonne ───

const MS_JOUR = 86_400_000

/**
 * N° de série, OF, PN : les graphies d'un export Windchill ne sont pas stables
 * (casse, espaces). En majuscules, deux graphies d'une même machine ne font
 * plus deux valeurs de filtre ni deux groupes.
 */
export const identifiant = (v: string | undefined): string => (v ?? '').trim().toUpperCase() || SANS_VALEUR

export function ageEnJours(issue: Issue, maintenant: number): number {
  const t = Date.parse(issue.dateDeclaration)
  return Number.isNaN(t) ? 0 : Math.max(0, Math.floor((maintenant - t) / MS_JOUR))
}

function tranche(jours: number): string {
  return jours < 7 ? BUCKETS_DATE[0] : jours <= 30 ? BUCKETS_DATE[1] : BUCKETS_DATE[2]
}

/** Les valeurs sous lesquelles un problème répond au filtre d'une colonne. */
export function valeursFiltre(col: ColonneId, issue: Issue, ctx: ContexteTableau): string[] {
  switch (col) {
    case 'metier':
      // Un problème de goulotte répond à MECA ET à GOULOTTE (23/10) : « Mon
      // périmètre » de Thibaut (ELEC + AUTOM + GOULOTTE) est un simple OU.
      return issue.sujet ? [issue.metier, issue.sujet] : [issue.metier]
    case 'machine':
      return [identifiant(issue.numeroSerie)]
    case 'of':
      return [identifiant(issue.of)]
    case 'pn':
      return [identifiant(issue.pn)]
    case 'modele':
      return [ctx.modeleDe(issue)]
    case 'famille':
      return [issue.famille || SANS_VALEUR]
    case 'phase':
      return [issue.phase]
    case 'criticite':
      return [issue.criticite]
    case 'statut':
      return [issue.statut]
    case 'badge':
      // Seuls les clos portent un badge ; les clos d'avant le Lot 3 n'en ont pas.
      return issue.badgesCloture?.length ? issue.badgesCloture : issue.statut === 'Clos' ? [SANS_BADGE] : []
    case 'declarant':
      return [issue.declarantNom || SANS_VALEUR]
    case 'date':
    case 'age':
      return [tranche(ageEnJours(issue, ctx.maintenant))]
    case 'id':
    case 'titre':
    case 'cloture':
    case 'etat':
      return []
  }
}

type CleTri = string | number | null

function cleTri(col: ColonneId, issue: Issue, ctx: ContexteTableau): CleTri {
  switch (col) {
    case 'id':
      return issue.id
    case 'titre':
      return cleRapprochement(issue.titre)
    case 'metier':
      return METIERS_ORDRE.indexOf(issue.metier)
    case 'machine':
      return issue.numeroSerie.trim().toUpperCase() || null
    case 'of':
      return issue.of.trim().toUpperCase() || null
    case 'pn':
      return issue.pn.trim().toUpperCase() || null
    case 'modele':
      return ctx.modeleDe(issue)
    case 'famille':
      return issue.famille || null
    case 'phase':
      return PHASES_ORDRE.indexOf(issue.phase)
    case 'criticite':
      return CRITICITES_ORDRE.indexOf(issue.criticite)
    case 'statut':
      return STATUTS_ORDRE.indexOf(issue.statut)
    case 'etat':
      // À qualifier, puis en cours du plus critique au moins critique, puis clos, annulés.
      return STATUTS_ORDRE.indexOf(issue.statut) * 10 + CRITICITES_ORDRE.indexOf(issue.criticite)
    case 'badge':
      return issue.badgesCloture?.length ? BADGES_CLOTURE.indexOf(issue.badgesCloture[0]!) : null
    case 'declarant':
      return issue.declarantNom || null
    case 'date':
      return Date.parse(issue.dateDeclaration) || null
    case 'age':
      return ageEnJours(issue, ctx.maintenant)
    case 'cloture':
      return issue.dateCloture ? Date.parse(issue.dateCloture) || null : null
  }
}

/** Le texte affiché, pour mesurer la largeur qu'il demande. */
export function texteMesure(col: ColonneId, issue: Issue, ctx: ContexteTableau): string {
  switch (col) {
    case 'id':
      return issue.id
    case 'metier':
      return METIERS_LABELS_COURT[issue.metier]
    case 'badge':
      return issue.badgesCloture?.length ? DEFINITIONS_BADGES[issue.badgesCloture[0]!].court : ''
    case 'date':
    case 'cloture':
      return '00/00/00'
    case 'age':
      return `${ageEnJours(issue, ctx.maintenant)} j`
    case 'modele':
      return ctx.modeleDe(issue)
    case 'declarant':
      return issue.declarantNom
    case 'titre':
      return issue.titre
    case 'etat':
      return ''
    default:
      return String(valeursFiltre(col, issue, ctx)[0] ?? '')
  }
}

// ─── Recherche, filtres ───

/** Sans accents, casse, espaces ni tirets : « 4825 sc » trouve « 4825-SC », « p20260042 » trouve P2026-0042. */
const sansAccent = (s: string): string =>
  s.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[\s\-_.]+/g, '')

export function correspondALaRecherche(issue: Issue, recherche: string): boolean {
  const t = sansAccent(recherche)
  if (!t) return true
  return [issue.id, issue.titre, issue.numeroSerie, issue.of, issue.pn, issue.declarantNom, issue.sujet].some((v) =>
    sansAccent(v ?? '').includes(t),
  )
}

/**
 * Le problème passe-t-il tous les filtres, sauf ceux de `sauf` (pour compter les
 * valeurs d'un filtre sans que lui-même ne les réduise) ?
 */
function passe(
  issue: Issue,
  r: ReglagesTableau,
  recherche: string,
  ctx: ContexteTableau,
  sauf: ColonneId | null,
): boolean {
  for (const [col, choisies] of Object.entries(r.filtres) as [ColonneId, string[]][]) {
    if (col === sauf || choisies.length === 0) continue
    if (!valeursFiltre(col, issue, ctx).some((v) => choisies.includes(v))) return false
  }
  if (r.mesDeclarations && !(ctx.mesIdentites && estDeclarePar(issue.declarant, ctx.mesIdentites))) return false
  if (r.suivis && !ctx.estSuivi(issue)) return false
  if (!correspondALaRecherche(issue, recherche)) return false
  // Les annulés ne reviennent qu'en cochant « Annulé » dans Statut.
  if (sauf !== 'statut' && !r.filtres.statut?.length && !compteCommeProbleme(issue)) return false
  return true
}

export function filtrer(
  issues: readonly Issue[],
  r: ReglagesTableau,
  recherche: string,
  ctx: ContexteTableau,
): Issue[] {
  return issues.filter((i) => passe(i, r, recherche, ctx, null))
}

/** Combien de problèmes par valeur d'un filtre, les AUTRES filtres appliqués. */
export function compterValeurs(
  issues: readonly Issue[],
  r: ReglagesTableau,
  recherche: string,
  ctx: ContexteTableau,
  col: ColonneId,
): Map<string, number> {
  const n = new Map<string, number>()
  for (const i of issues) {
    if (!passe(i, r, recherche, ctx, col)) continue
    for (const v of new Set(valeursFiltre(col, i, ctx))) n.set(v, (n.get(v) ?? 0) + 1)
  }
  return n
}

/** Toutes les valeurs possibles d'un filtre, dans l'ordre canonique ou alphabétique. */
export function valeursPossibles(issues: readonly Issue[], col: ColonneId, ctx: ContexteTableau): string[] {
  const def = DEFINITIONS_COLONNES[col]
  if (def.valeurs) return [...def.valeurs]
  const vus = new Set<string>()
  for (const i of issues) for (const v of valeursFiltre(col, i, ctx)) vus.add(v)
  return [...vus].sort((a, b) => a.localeCompare(b, 'fr', { numeric: true }))
}

/** Combien de valeurs coche l'entonnoir d'une colonne (l'État porte Statut et Criticité). */
export function nbValeursFiltrees(r: ReglagesTableau, col: ColonneId): number {
  const cols = col === 'etat' ? FILTRES_ETAT : [col]
  return cols.reduce((t, c) => t + (r.filtres[c]?.length ?? 0), 0)
}

export const nbFiltresActifs = (r: ReglagesTableau): number =>
  Object.values(r.filtres).filter((v) => v && v.length > 0).length + (r.mesDeclarations ? 1 : 0) + (r.suivis ? 1 : 0)

export function basculerValeur(filtres: FiltresColonnes, col: ColonneId, valeur: string, actif: boolean): FiltresColonnes {
  const actuelles = filtres[col] ?? []
  const suivantes = actif ? [...new Set([...actuelles, valeur])] : actuelles.filter((v) => v !== valeur)
  const { [col]: _retire, ...reste } = filtres
  void _retire
  return suivantes.length > 0 ? { ...reste, [col]: suivantes } : reste
}

// ─── Tri ───

export const TRI_DEFAUT: readonly CritereTri[] = [{ col: 'date', sens: 'desc' }]

export const triEffectif = (r: ReglagesTableau): readonly CritereTri[] => (r.tri.length > 0 ? r.tri : TRI_DEFAUT)

export const triParDefaut = (r: ReglagesTableau): boolean =>
  r.tri.length === 0 || (r.tri.length === 1 && r.tri[0]!.col === 'date' && r.tri[0]!.sens === 'desc')

/**
 * Clic sur un en-tête. Seul : premier clic = sens naturel de la colonne, deuxième
 * = inverse, troisième = retour au tri par défaut. Avec Maj : ajoute la colonne
 * comme tri secondaire (puis l'inverse, puis la retire).
 */
export function trierPar(tri: readonly CritereTri[], col: ColonneId, ajouter: boolean): CritereTri[] {
  const premier = DEFINITIONS_COLONNES[col].premierSens
  const second: SensTri = premier === 'asc' ? 'desc' : 'asc'
  const actuel = tri.find((t) => t.col === col)
  if (ajouter) {
    if (!actuel) return [...tri, { col, sens: premier }]
    if (actuel.sens === premier) return tri.map((t) => (t.col === col ? { col, sens: second } : t))
    return tri.filter((t) => t.col !== col)
  }
  if (actuel && tri.length === 1) return actuel.sens === premier ? [{ col, sens: second }] : []
  return [{ col, sens: premier }]
}

export function trier(issues: readonly Issue[], tri: readonly CritereTri[], ctx: ContexteTableau): Issue[] {
  const critere = tri.length > 0 ? tri : TRI_DEFAUT
  const cles = new Map<Issue, CleTri[]>()
  for (const i of issues) cles.set(i, critere.map((c) => cleTri(c.col, i, ctx)))
  return issues.slice().sort((a, b) => {
    const ka = cles.get(a)!
    const kb = cles.get(b)!
    for (let n = 0; n < critere.length; n++) {
      const va = ka[n]
      const vb = kb[n]
      // Une valeur absente va toujours à la fin, quel que soit le sens.
      if (va == null && vb == null) continue
      if (va == null) return 1
      if (vb == null) return -1
      const c =
        typeof va === 'number' && typeof vb === 'number'
          ? va - vb
          : String(va).localeCompare(String(vb), 'fr', { numeric: true })
      if (c !== 0) return critere[n]!.sens === 'asc' ? c : -c
    }
    return (Date.parse(b.dateDeclaration) || 0) - (Date.parse(a.dateDeclaration) || 0) || a.id.localeCompare(b.id)
  })
}

// ─── Regroupement ───

export type EntreeTableau =
  | { type: 'groupe'; cle: string; items: Issue[] }
  /** `soeurs` : les fiches d'un même défaut multi-équipements sur cette rangée (cf. fusionnerSoeurs). */
  | { type: 'ligne'; issue: Issue; soeurs?: Issue[] }

// ─── Un défaut sur plusieurs équipements (26/10) ───
//
// Maquette : https://claude.ai/artifact/JPGMp9Yx21HzKSok5x2YAL (option A). Les
// fiches sœurs (colonne Groupe, Lot 14) tiennent sur UNE rangée, à la place de la
// mieux classée : le titre une fois, une étiquette par équipement avec son état et
// son badge. Décisions de Guillaume : une sœur écartée par un filtre reste cachée ;
// la colonne État dit l'état le moins avancé ; le badge est celui de l'équipement
// concerné, dans son étiquette. Le compte des problèmes ne change pas (un défaut
// sur 3 équipements compte 3) ; regroupé par N° de série, on ne fusionne pas.

export interface RangeeFusionnee {
  issue: Issue
  /** Toutes les fiches sœurs de la liste, celle de la rangée comprise, par ID ; absent pour une fiche seule. */
  soeurs?: Issue[]
}

export function fusionnerSoeurs(rows: readonly Issue[]): RangeeFusionnee[] {
  const parGroupe = new Map<string, Issue[]>()
  for (const i of rows) {
    const g = cleRapprochement(i.groupe)
    if (!g) continue
    const liste = parGroupe.get(g)
    if (liste) liste.push(i)
    else parGroupe.set(g, [i])
  }
  const vus = new Set<string>()
  const resultat: RangeeFusionnee[] = []
  for (const i of rows) {
    const g = cleRapprochement(i.groupe)
    const membres = g ? parGroupe.get(g)! : []
    if (membres.length < 2) {
      resultat.push({ issue: i })
      continue
    }
    if (vus.has(g)) continue
    vus.add(g)
    resultat.push({ issue: i, soeurs: membres.slice().sort((a, b) => a.id.localeCompare(b.id)) })
  }
  return resultat
}

/** La fiche la moins avancée (statut, puis criticité la plus haute) : c'est elle que montre la colonne État. */
export function moinsAvancee(soeurs: readonly Issue[]): Issue {
  return soeurs.reduce((a, b) => {
    const d = STATUTS_ORDRE.indexOf(b.statut) - STATUTS_ORDRE.indexOf(a.statut)
    if (d !== 0) return d < 0 ? b : a
    return CRITICITES_ORDRE.indexOf(b.criticite) < CRITICITES_ORDRE.indexOf(a.criticite) ? b : a
  })
}

/** Les fiches sœurs ont-elles la même valeur dans cette colonne ? Sinon la cellule dit « Plusieurs ». */
export function valeurCommune(col: ColonneId, soeurs: readonly Issue[], ctx: ContexteTableau): boolean {
  const valeur = (i: Issue): string => {
    switch (col) {
      case 'date':
        return i.dateDeclaration.slice(0, 10)
      case 'cloture':
        return (i.dateCloture ?? '').slice(0, 10)
      case 'machine':
      case 'of':
      case 'pn':
        return identifiant(String(valeursFiltre(col, i, ctx)[0] ?? ''))
      default:
        return texteMesure(col, i, ctx)
    }
  }
  const premiere = valeur(soeurs[0]!)
  return soeurs.every((i) => valeur(i) === premiere)
}

const enLignes = (rows: readonly Issue[], mode: Regroupement | null): EntreeTableau[] =>
  (mode === 'machine' ? rows.map((issue): RangeeFusionnee => ({ issue })) : fusionnerSoeurs(rows)).map(
    (r): EntreeTableau => ({ type: 'ligne', ...r }),
  )

export function cleDeGroupe(mode: Regroupement, issue: Issue): string {
  switch (mode) {
    case 'machine':
      return identifiant(issue.numeroSerie)
    case 'pn':
      return identifiant(issue.pn)
    case 'metier':
      return issue.metier
    case 'statut':
      return issue.statut
  }
}

/**
 * Un titre par groupe, suivi de ses problèmes (sauf groupe replié). Machines et
 * PN prennent l'ordre du tri : le groupe du premier problème en tête. Métiers et
 * statuts gardent leur ordre habituel.
 */
export function regrouper(
  rows: readonly Issue[],
  mode: Regroupement | null,
  repliees: ReadonlySet<string>,
): EntreeTableau[] {
  if (!mode) return enLignes(rows, mode)
  const parCle = new Map<string, Issue[]>()
  for (const i of rows) {
    const cle = cleDeGroupe(mode, i)
    const liste = parCle.get(cle)
    if (liste) liste.push(i)
    else parCle.set(cle, [i])
  }
  let cles = [...parCle.keys()]
  if (mode === 'metier') cles = cles.sort((a, b) => METIERS_ORDRE.indexOf(a as Metier) - METIERS_ORDRE.indexOf(b as Metier))
  if (mode === 'statut') cles = cles.sort((a, b) => STATUTS_ORDRE.indexOf(a as never) - STATUTS_ORDRE.indexOf(b as never))
  return cles.flatMap((cle): EntreeTableau[] => {
    const items = parCle.get(cle)!
    return [
      { type: 'groupe', cle, items },
      ...(repliees.has(cle) ? [] : enLignes(items, mode)),
    ]
  })
}

export interface ResumeGroupe {
  nbProblemes: number
  nbActifs: number
  nbMachines: number
  /** Criticité la plus haute parmi les problèmes actifs déjà qualifiés ; sinon À qualifier ; null si aucun actif. */
  criticiteMax: Issue['criticite'] | null
  /** Ancienneté (jours) du plus ancien problème actif. */
  plusAncienJours: number
  /** Modèle et PN, pour le titre d'une machine. */
  modele: string
  pn: string
}

export function resumerGroupe(items: readonly Issue[], ctx: ContexteTableau): ResumeGroupe {
  const actifs = items.filter((i) => i.statut === 'Ouvert' || i.statut === 'En cours')
  let criticiteMax: Issue['criticite'] | null = null
  let plusAncienJours = 0
  for (const i of actifs) {
    if (criticiteMax === null || CRITICITES_ORDRE.indexOf(i.criticite) < CRITICITES_ORDRE.indexOf(criticiteMax)) {
      criticiteMax = i.criticite
    }
    plusAncienJours = Math.max(plusAncienJours, ageEnJours(i, ctx.maintenant))
  }
  const premier = items[0]
  return {
    nbProblemes: items.length,
    nbActifs: actifs.length,
    nbMachines: new Set(items.map((i) => identifiant(i.numeroSerie))).size,
    criticiteMax,
    plusAncienJours,
    modele: premier ? ctx.modeleDe(premier) : '',
    pn: premier?.pn ?? '',
  }
}

// ─── Largeurs ───

export const LARGEUR_LISERE = 3
export const LARGEUR_TITRE_AUTO = 240
export const LARGEUR_TITRE_SERRE = 180

/**
 * Largeur automatique d'une colonne, à partir de la largeur (px) des textes
 * qu'elle contient et de celle de son libellé. Jamais moins que l'en-tête avec
 * ses icônes de tri et de filtre, jamais hors des bornes de la colonne.
 */
export function largeurAuto(def: DefColonne, largeursTextes: readonly number[], largeurLibelle: number): number {
  const triees = largeursTextes.slice().sort((a, b) => a - b)
  const corps =
    triees.length === 0 ? 0 : def.vocabulaireFerme ? triees[triees.length - 1]! : triees[Math.floor((triees.length - 1) * 0.9)]!
  // Le contenu est plafonné ; l'en-tête, lui, ne l'est jamais : un libellé
  // coupé (« DÉCLAR… ») est pire qu'une colonne un peu large.
  const entete = Math.ceil(largeurLibelle + (def.filtrable ? 70 : 44))
  const contenu = Math.min(def.max, Math.ceil(corps + def.extra + 24))
  return Math.max(def.min, contenu, entete)
}

/**
 * Largeurs définitives des colonnes visibles pour la place disponible. Une
 * largeur fixée à la main n'est jamais touchée. Place insuffisante : le titre
 * cède d'abord (jusqu'à 180 px), puis les colonnes de TEXTE LIBRE (modèle,
 * famille, déclarant) vers leur minimum, au prorata de ce qu'elles peuvent
 * céder ; au-delà, le tableau défile. Les colonnes à vocabulaire fermé (statut,
 * dates, références) ne se serrent jamais : une référence ou un badge coupé ne
 * se lit plus.
 */
export function repartirLargeurs(
  visibles: readonly ColonneId[],
  auto: Partial<Record<ColonneId, number>>,
  manuelles: Partial<Record<ColonneId, number>>,
  disponible: number,
): { largeurs: Partial<Record<ColonneId, number>>; total: number } {
  const largeurs: Partial<Record<ColonneId, number>> = {}
  for (const c of visibles) {
    largeurs[c] =
      c === 'titre' ? LARGEUR_TITRE_AUTO : (manuelles[c] ?? auto[c] ?? DEFINITIONS_COLONNES[c].min)
  }
  const somme = (): number => LARGEUR_LISERE + visibles.reduce((t, c) => t + (largeurs[c] ?? 0), 0)
  let exces = somme() - disponible
  if (disponible > 0 && exces > 0) {
    if (largeurs.titre !== undefined) {
      const rogne = Math.min(exces, largeurs.titre - LARGEUR_TITRE_SERRE)
      largeurs.titre -= rogne
      exces -= rogne
    }
    if (exces > 0) {
      const souples = visibles.filter(
        (c) =>
          c !== 'titre' &&
          manuelles[c] === undefined &&
          !DEFINITIONS_COLONNES[c].vocabulaireFerme &&
          (largeurs[c] ?? 0) > DEFINITIONS_COLONNES[c].min,
      )
      const marge = souples.reduce((t, c) => t + (largeurs[c] ?? 0) - DEFINITIONS_COLONNES[c].min, 0)
      if (marge > 0) {
        const part = Math.min(1, exces / marge)
        for (const c of souples) {
          const l = largeurs[c] ?? 0
          largeurs[c] = Math.round(l - (l - DEFINITIONS_COLONNES[c].min) * part)
        }
      }
    }
  }
  return { largeurs, total: somme() }
}

/** Gabarit CSS : liseré, puis les colonnes ; le titre prend le reste. */
export function gabaritColonnes(visibles: readonly ColonneId[], largeurs: Partial<Record<ColonneId, number>>): string {
  return [
    `${LARGEUR_LISERE}px`,
    ...visibles.map((c) => (c === 'titre' ? `minmax(${largeurs[c] ?? LARGEUR_TITRE_AUTO}px, 1fr)` : `${largeurs[c] ?? DEFINITIONS_COLONNES[c].min}px`)),
  ].join(' ')
}

// ─── Colonnes : gestes du menu ───

export function deplacerColonne(ordre: readonly ColonneId[], col: ColonneId, delta: -1 | 1): ColonneId[] {
  const i = ordre.indexOf(col)
  const j = i + delta
  if (i < 0 || j < 0 || j >= ordre.length) return [...ordre]
  const suite = [...ordre]
  suite.splice(i, 1)
  suite.splice(j, 0, col)
  return suite
}

/** Glisser-déposer : `col` prend la place de `avant`. */
export function placerAvant(ordre: readonly ColonneId[], col: ColonneId, avant: ColonneId): ColonneId[] {
  if (col === avant) return [...ordre]
  const suite = ordre.filter((c) => c !== col)
  const at = suite.indexOf(avant)
  suite.splice(at < 0 ? suite.length : at, 0, col)
  return suite
}

export function basculerColonne(visibles: readonly ColonneId[], col: ColonneId, visible: boolean): ColonneId[] {
  if (DEFINITIONS_COLONNES[col].verrouillee) return [...visibles]
  return visible ? (visibles.includes(col) ? [...visibles] : [...visibles, col]) : visibles.filter((c) => c !== col)
}

/** Les colonnes à afficher, dans l'ordre choisi. */
export function colonnesAffichees(r: ReglagesTableau, interne: boolean): ColonneId[] {
  return r.ordre.filter((c) => r.visibles.includes(c) && (interne || !DEFINITIONS_COLONNES[c].referentiel))
}

// ─── Vues prêtes à l'emploi ───

export interface VuePredefinie {
  id: string
  libelle: string
  filtres: FiltresColonnes
  mesDeclarations?: boolean
  suivis?: boolean
  tri?: CritereTri[]
  /** Vue personnelle : elle impose aussi son regroupement, et peut être supprimée. */
  perso?: boolean
  regroupement?: Regroupement | null
}

/**
 * Tous · Mon périmètre (coordinateur ou suppléant, et sujets dont on est
 * responsable) · À qualifier · Goulottes (qui les traite ou les pilote, 23/10) ·
 * Suivis (internes) — puis les vues personnelles. « Critiques en cours » et « Mes
 * déclarations » ont été retirées le 02/10 : chacun se fait les siennes.
 */
export function vuesPredefinies(opts: {
  metiersPerimetre: readonly ClePerimetre[]
  peutSuivre: boolean
  /** Sujets dont on propose la vue (Goulottes). */
  sujets?: readonly Sujet[]
}): VuePredefinie[] {
  const vues: VuePredefinie[] = [{ id: 'tous', libelle: 'Tous', filtres: {} }]
  if (opts.metiersPerimetre.length > 0) {
    vues.push({
      id: 'perimetre',
      libelle: `Mon périmètre · ${opts.metiersPerimetre.map(libelleCle).join(' + ')}`,
      filtres: { metier: [...opts.metiersPerimetre] },
    })
  }
  vues.push({ id: 'a-qualifier', libelle: 'À qualifier', filtres: { statut: ['Ouvert'] }, tri: [{ col: 'date', sens: 'asc' }] })
  for (const sujet of opts.sujets ?? []) {
    vues.push({ id: `sujet-${sujet}`, libelle: SUJETS_CONFIG[sujet].libellePluriel, filtres: { metier: [sujet] } })
  }
  if (opts.peutSuivre) vues.push({ id: 'suivis', libelle: 'Suivis', filtres: {}, suivis: true })
  return vues
}

export function vuesPersonnelles(r: ReglagesTableau): VuePredefinie[] {
  return r.vues.map((v) => ({
    id: `perso:${v.id}`,
    libelle: v.nom,
    filtres: v.filtres,
    tri: v.tri,
    regroupement: v.regroupement,
    mesDeclarations: v.mesDeclarations,
    suivis: v.suivis,
    perso: true,
  }))
}

const memesFiltres = (a: FiltresColonnes, b: FiltresColonnes): boolean => {
  const norm = (f: FiltresColonnes): string =>
    JSON.stringify(
      Object.entries(f)
        .filter(([, v]) => v && v.length > 0)
        .map(([k, v]) => [k, [...(v ?? [])].sort()] as const)
        .sort(([x], [y]) => x.localeCompare(y)),
    )
  return norm(a) === norm(b)
}

export function appliquerVue(r: ReglagesTableau, vue: VuePredefinie): ReglagesTableau {
  return {
    ...r,
    filtres: JSON.parse(JSON.stringify(vue.filtres)) as FiltresColonnes,
    mesDeclarations: vue.mesDeclarations === true,
    suivis: vue.suivis === true,
    tri: vue.tri ? vue.tri.map((t) => ({ ...t })) : [],
    ...(vue.regroupement !== undefined ? { regroupement: vue.regroupement } : {}),
  }
}

/** La vue dont les filtres sont exactement ceux en cours (une vue perso l'emporte sur une vue fixe). */
export function vueActive(r: ReglagesTableau, vues: readonly VuePredefinie[]): VuePredefinie | null {
  const correspond = (v: VuePredefinie): boolean =>
    memesFiltres(v.filtres, r.filtres) &&
    (v.mesDeclarations === true) === r.mesDeclarations &&
    (v.suivis === true) === r.suivis
  return vues.find((v) => v.perso && correspond(v)) ?? vues.find(correspond) ?? null
}

/** Pourquoi ce nom ne convient pas, ou null s'il convient. */
export function problemeNomVue(nom: string, r: ReglagesTableau, fixes: readonly VuePredefinie[]): string | null {
  const n = nom.trim()
  if (!n) return 'Donnez un nom à la vue.'
  if (n.length > LONGUEUR_MAX_NOM_VUE) return `${LONGUEUR_MAX_NOM_VUE} caractères au plus.`
  const cle = n.toLocaleLowerCase('fr')
  if (r.vues.some((v) => v.nom.toLocaleLowerCase('fr') === cle) || fixes.some((v) => v.libelle.toLocaleLowerCase('fr') === cle)) {
    return 'Une vue porte déjà ce nom.'
  }
  if (r.vues.length >= MAX_VUES_PERSO) return `${MAX_VUES_PERSO} vues au plus : supprimez-en une d’abord.`
  return null
}

/** Enregistre les filtres, le tri et le regroupement en cours sous un nom. */
export function enregistrerVue(r: ReglagesTableau, nom: string, id: string): ReglagesTableau {
  const vue: VuePerso = {
    id,
    nom: nom.trim(),
    filtres: JSON.parse(JSON.stringify(r.filtres)) as FiltresColonnes,
    tri: r.tri.map((t) => ({ ...t })),
    regroupement: r.regroupement,
    mesDeclarations: r.mesDeclarations,
    suivis: r.suivis,
  }
  return { ...r, vues: [...r.vues, vue] }
}

export function supprimerVue(r: ReglagesTableau, idVue: string): ReglagesTableau {
  const id = idVue.startsWith('perso:') ? idVue.slice('perso:'.length) : idVue
  return { ...r, vues: r.vues.filter((v) => v.id !== id) }
}

/** Combien de problèmes une vue montre (recherche ignorée, annulés exclus). */
export function compterVue(issues: readonly Issue[], vue: VuePredefinie, ctx: ContexteTableau): number {
  const r = appliquerVue(reglagesParDefaut(), vue)
  return issues.filter((i) => passe(i, r, '', ctx, null)).length
}
